import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useEquipamentoMedicaoListaPagination } from "../hooks/pagination";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useSearchParams } from "react-router";
import { useEquipamentoMedicaoListaFilters } from "../hooks/filter";
import { EquipamentoMedicaoList } from "../../domain/models/EquipamentoMedicao_List";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { EquipamentoMedicaoForm } from "../schemas/equipamentoMedicao";
import { EquipamentoMedicaoHistorico } from "../../../../common/domain/models/equipamento_medicao_historico";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { CopyEquipamentoMedicaoForm } from "../schemas/EquipamentoMedicao_lista/copy_equipamento_medicao";

type LoadEquipamentoMedicaoFuncState = {
  idObra?: number | null;
  idsCentroCusto?: number[] | null;
  status?: string | null;
  equipamento?: string | null;
  responsavelPeloEquipamento?: string | null;
  dataCalibracaoDe?: Date | null;
  dataCalibracaoAte?: Date | null;

  page?: number | null;
  rowsPerPage?: number | null;
};

export const useEquipamentoMedicaoListaViewModel = () => {
  const {
    commons: {
      getCentroCustoDropDownUseCase,
      getFuncionariosDropDownUseCase,
    },
    equipamentoMedicao: {
      getAllEquipamentoMedicaoListUseCase,
      createEquipamentoMedicaoUseCase,
      deleteEquipamentoMedicoesUseCase,
      updateEquipamentoMedicoesUseCase,
      getByIdEquipamentoMedicaoUseCase,
      downloadEquipamentoMedicaoArquivoUseCase,
      getAllEquipamentoMedicaoHistoricoUseCase,
    },
  } = useDependencyInjection();

  const [searchParams] = useSearchParams();
  const tipoMonitoramento = searchParams.get('tipoMonitoramento');
  const filtroAtraso = searchParams.get('filtroAtraso');

  const { pushErrorNotification, setCustomLoading } = usePageLoader();
  const backOrNavigate = useBackOrNavigate();

  const pagination = useEquipamentoMedicaoListaPagination();
  const debouncedRef = useCreateDebounceHandler(750);

  const [errorMessage, setErrorMessage] = useState<string>();
  const filters = useEquipamentoMedicaoListaFilters();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [equipamentoMedicaoLista, setEquipamentoMedicaoLista] = useState<EquipamentoMedicaoList[]>();
  const [equipamentoMedicaoHistoricoLista, setEquipamentoMedicaoHistoricoLista] = useState<EquipamentoMedicaoHistorico[]>();

  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openHistoricoModal, setOpenHistoricoModal] = useState(false);
  const [openCopyModal, setOpenCopyModal] = useState(false);

  const [selectedCopyIdEquipamentoMedicao, setSelectedCopyIdEquipamentoMedicao] = useState<number | null>(null);
  const [ddlFuncionarios, setDdlFuncionarios] = useState<FuncionarioDropDown[]>([]);

  const [selectedEquipamentoMedicao, setSelectedEquipamentoMedicao] = useState<EquipamentoMedicaoForm | null>(null);
  const [selectedIdEquipamentoMedicao, setSelectedIdEquipamentoMedicao] = useState<number>();

  const [ddlCentroCusto, setDdlCentroCusto] = useState<DropDown[]>();


  //#region  PAGE START 

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropDown();
      await loadEquipamentoMedicaoLista();

    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das paginas!")[0]);
    }
  });

  //#endregion

  //#region  CARREGAMENTO
  const loadEquipamentoMedicaoLista = useCallback(
    async (funcState?: LoadEquipamentoMedicaoFuncState) => {
      try {
        const filteredequipamento = evaluateParamValue(funcState?.equipamento, filters.equipamento);
        const filteredresponsavel = evaluateParamValue(funcState?.responsavelPeloEquipamento, filters.responsavelPeloEquipamento);
        const filterStatus = evaluateParamValue(funcState?.status, filters.status);
        const filterDataCalibracaode = evaluateParamValue(funcState?.dataCalibracaoDe, filters.dataCalibracaoDe);
        const filterDataCalibracaoate = evaluateParamValue(funcState?.dataCalibracaoAte, filters.dataCalibracaoAte);
        const filteridsCentroCusto = evaluateParamValue(funcState?.idsCentroCusto, filters.idsCentroCusto);

        const filterpage = funcState?.page ?? pagination.page;
        const filterRowspage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllEquipamentoMedicaoListUseCase.getAllEquipamentoMedicaoList(
          null,
          filteridsCentroCusto ?? [],
          filterStatus,
          filteredequipamento,
          filteredresponsavel,
          filterDataCalibracaode,
          filterDataCalibracaoate,

          filterpage,
          filterRowspage
        );
        let listagem = getAllEquipamentoMedicaoListUseCase.items;

        if (tipoMonitoramento === 'C') {
          listagem = listagem.filter(item => item.tipo === "Calibrado");
        } else if (tipoMonitoramento === 'V') {
          listagem = listagem.filter(item => item.tipo === "Verificação");
        }

        if (filtroAtraso === 'true') {
          listagem = listagem.filter(item => item.status === "A");
          filters.setFilters({ status: "A" });
        }

        setEquipamentoMedicaoLista(listagem);

        pagination.setRowsPerPage(getAllEquipamentoMedicaoListUseCase.pageSize);
        pagination.setTotalItems(getAllEquipamentoMedicaoListUseCase.totalItems);
      } catch (e: unknown) {
        setEquipamentoMedicaoLista([]);
        throw new Error("Falha ao buscar equipamentos de medição!");
      }
    }, [
    filters,
    filtroAtraso,
    pagination,
    getAllEquipamentoMedicaoListUseCase,
    tipoMonitoramento,
  ]);


  const loadEquipamentoMedicaoById = useCallback(async (id: number) => {
    setLoading(true);
    try {

      const response = await getByIdEquipamentoMedicaoUseCase.getByIdEquipamentoMedicao(id);

      setSelectedEquipamentoMedicao({
        idEquipamentoMedicao: response.idEquipamentoMedicao,
        equipamento: response.equipamento ?? "",
        idFuncionario: response.idFuncionario ?? undefined,
        idFuncionarioMonit: response.idFuncionarioMonit ?? undefined,
        descResponsavelMonit: response.descResponsavelMonit ?? "",
        responsavelCadastradoMonit: response.responsavelCadastradoMonit ?? "",
        frequenciaVerificao: response.frequenciaVerificao ?? null,
        idFuncionarioResp: response.idFuncionarioResp ?? null,
        descResponsavelResp: response.descResponsavelResp ?? "",
        responsavelCadastradoResp: response.responsavelCadastradoResp ?? false, // obrigatório
        dataVerificacao: response.dataVerificacao ? new Date(response.dataVerificacao) : null,
        dataProxVerificacao: response.dataProxVerificacao ? new Date(response.dataProxVerificacao) : null,
        tipoMonitoramento: response.tipoMonitoramento ?? "V",
        nomeArquivo: response.nomeArquivo ?? null,
        nomeArquivo1: response.nomeArquivo1 ?? null,
        observacao: response.observacao ?? "",
        idCentroCusto: response.idCentroCusto ?? null,
        idFornecedorMonit: response.idFornecedorMonit ?? null,
        tipoVerificacao: response.tipoVerificacao ?? false,
        tipoRespEquipamento: response.idFuncionarioResp ? 0 : 1,
        tipoCaliVeriiPor:
          response.idFornecedorMonit != null
            ? 0
            : response.idFuncionarioMonit != null
              ? 1
              : response.descResponsavelMonit != null
                ? 3
                : null,
        tipoFrequenciaData: response.frequenciaVerificao === 1 ? 1 : 0,
        limparArquivos: false,
        limparArquivos1: false,
        cor: response.cor ?? null,
      });
    } catch (error) {
      pushErrorNotification("Erro", "Falha ao buscar dados do equipamento de medição!");
    } finally {
      setLoading(false);
    }
  },
    [
      getByIdEquipamentoMedicaoUseCase,
      pushErrorNotification,
    ]
  );

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const centroCusto = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      setDdlCentroCusto(centroCusto);
    } catch {
      pushErrorNotification("Erro", "Falha ao buscar centro de custo para filtro!");
    }
  }, [getCentroCustoDropDownUseCase, pushErrorNotification]);


  //#endregion


  //#region CADASTRO E LEITURA

  const novoEquipamento = () => {
    setSelectedEquipamentoMedicao(null); // garante que não tem dados antigos
    setOpenCreateModal(true);
  };

  const handleEquipamentoMedicaoCadastroOpenState = useCallback(async (idEquipMedicao: number | null, openState: boolean) => {
    setOpenCreateModal(openState);
    if (openState) {
      if (idEquipMedicao && equipamentoMedicaoLista) {
        equipamentoMedicaoLista.find((x) => x.idEquipMedicao === idEquipMedicao);
        await loadEquipamentoMedicaoById(idEquipMedicao);
      }
    } else {
      setSelectedEquipamentoMedicao(null);
    }
  },
    [equipamentoMedicaoLista, loadEquipamentoMedicaoById]);

  const handleEquipamentoMedicaoSubmit = useCallback(
    async (form: EquipamentoMedicaoForm) => {
      try {
        setSaving(true);
        if (!selectedEquipamentoMedicao) {
          const idCreatedEquipamentoMedicao = await createEquipamentoMedicaoUseCase.createEquipamentoMedicao(
            null,
            form.equipamento,
            form.idFuncionario,
            form.idFuncionarioMonit,
            form.descResponsavelMonit,
            form.responsavelCadastradoMonit,
            form.frequenciaVerificao,
            form.idFuncionarioResp,
            form.descResponsavelResp,
            form.responsavelCadastradoResp,
            form.dataVerificacao,
            form.dataProxVerificacao,
            form.tipoMonitoramento,
            form.arquivo,
            form.nomeArquivo,
            form.arquivo1,
            form.nomeArquivo1,
            form.observacao,
            form.idCentroCusto,
            form.idFornecedorMonit,
            form.tipoVerificacao,
            form.tipoRespEquipamento,
            form.tipoCaliVeriiPor,
            form.tipoFrequenciaData
          );

          await loadEquipamentoMedicaoById(idCreatedEquipamentoMedicao);
        } else {
          if (form.idEquipamentoMedicao != null) {
            await updateEquipamentoMedicoesUseCase.updateEquipamentoMedicao(
              form.idEquipamentoMedicao,
              null,
              form.equipamento,
              form.idFuncionario,
              form.idFuncionarioMonit,
              form.descResponsavelMonit,
              form.responsavelCadastradoMonit,
              form.frequenciaVerificao,
              form.idFuncionarioResp,
              form.descResponsavelResp,
              form.responsavelCadastradoResp,
              form.dataVerificacao,
              form.dataProxVerificacao,
              form.tipoMonitoramento,
              form.arquivo,
              form.nomeArquivo,
              form.arquivo1,
              form.nomeArquivo1,
              form.observacao,
              form.idCentroCusto,
              form.idFornecedorMonit,
              form.tipoVerificacao,
              form.tipoRespEquipamento,
              form.tipoCaliVeriiPor,
              form.tipoFrequenciaData,
              form.limparArquivos,
              form.limparArquivos1
            );

            if (selectedEquipamentoMedicao && selectedEquipamentoMedicao.idEquipamentoMedicao) {
              await loadEquipamentoMedicaoById(selectedEquipamentoMedicao.idEquipamentoMedicao);
            }
          }
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar documentos!"));
      } finally {
        setSaving(false);

        await loadEquipamentoMedicaoLista();
      }
    }, [
    selectedEquipamentoMedicao,
    createEquipamentoMedicaoUseCase,
    updateEquipamentoMedicoesUseCase,
    loadEquipamentoMedicaoById,
    loadEquipamentoMedicaoLista,
    pushErrorNotification
  ]);


  //#endregion

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: "/Obras_lista" });
  }, [backOrNavigate]);


  //#region DELETE

  const handleDeleteEquipamentoMedicaoOpenState = useCallback(
    (idEquipamentoMedicao: number | null, openState: boolean) => {
      if (openState) {
        if (idEquipamentoMedicao != null) {
          setSelectedIdEquipamentoMedicao(idEquipamentoMedicao);
        } else {
          setSelectedIdEquipamentoMedicao(undefined);
        }
      }
      setOpenDeleteModal(openState);
    },
    []
  );

  const handleDeleteEquipamentoMedicaoSubmit = useCallback(
    async () => {
      setLoading(true);
      if (selectedIdEquipamentoMedicao === undefined) return;
      try {
        await deleteEquipamentoMedicoesUseCase.deleteEquipamentoMedicoes(selectedIdEquipamentoMedicao);
        await loadEquipamentoMedicaoLista();
      } catch {
        pushErrorNotification("Erro", "Falha ao deletar o equipamento de medição!");
      } finally {
        setLoading(false);
        setOpenDeleteModal(false);
      }
    },
    [selectedIdEquipamentoMedicao, deleteEquipamentoMedicoesUseCase, loadEquipamentoMedicaoLista, pushErrorNotification]);


  //#endregion



  //#region PAGINATION E PESQUISA

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadEquipamentoMedicaoLista();
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de equipamentos de medição!'));
    } finally {
      setLoading(false);
    }
  }, [loadEquipamentoMedicaoLista, pushErrorNotification]);


  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);
      await loadEquipamentoMedicaoLista({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de equipamentos de medição!'));
    } finally {
      setLoading(false);
    }
  }, [pagination, loadEquipamentoMedicaoLista, pushErrorNotification]);


  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);
      await loadEquipamentoMedicaoLista({ rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de equipamentos de medição!'));
    } finally {
      setLoading(false);
    }
  }, [pagination, loadEquipamentoMedicaoLista, pushErrorNotification]);


  const handleDataCalibracaoRangeChange = useDebouncedCallback(
    debouncedRef,
    (dataCalibracaoDe: Date | null, dataCalibracaoAte: Date | null) => {
      filters.setFilters({ dataCalibracaoDe });
      filters.setFilters({ dataCalibracaoAte });

      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadEquipamentoMedicaoLista({ dataCalibracaoDe, dataCalibracaoAte, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar equipamentos de medição!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [
      loadEquipamentoMedicaoLista,
      filters,
      pagination]);

  const handleEquipamentoChange = useDebouncedCallback(
    debouncedRef,
    (newValue: string | null) => {
      filters.setFilters({ equipamento: newValue });
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadEquipamentoMedicaoLista({ equipamento: newValue, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar equipamentos de medição!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadEquipamentoMedicaoLista, filters]
  );


  const handleResponsavelPeloEquipamentoChange = useDebouncedCallback(
    debouncedRef,
    (novoResponsavel: string | null) => {
      filters.setFilters({ responsavelPeloEquipamento: novoResponsavel });
      return async () => {
        try {
          await loadEquipamentoMedicaoLista({ responsavelPeloEquipamento: novoResponsavel });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        }
      };
    },
    [filters, loadEquipamentoMedicaoLista, pushErrorNotification]
  );


  const handleStatusChange = useDebouncedCallback(
    debouncedRef,
    (novoStatus: string | null) => {
      filters.setFilters({ status: novoStatus ?? "T" });
      return async () => {
        try {
          await loadEquipamentoMedicaoLista({ status: novoStatus ?? "T" });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        }
      };
    },
    [filters, loadEquipamentoMedicaoLista, pushErrorNotification]
  );


  const handleIdsCentroCustoChange = useDebouncedCallback(
    debouncedRef,
    (newValues: number[]) => {
      filters.setFilters({ idsCentroCusto: newValues })
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadEquipamentoMedicaoLista({ idsCentroCusto: newValues, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar equipamentos de medição!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [
      filters,
      pagination,
      loadEquipamentoMedicaoLista
    ]);

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      filters.clearFilters();
      pagination.setPage(1);
      await loadEquipamentoMedicaoLista({
        idObra: null,
        idsCentroCusto: null,
        status: "T",
        equipamento: null,
        responsavelPeloEquipamento: null,
        dataCalibracaoDe: null,
        dataCalibracaoAte: null,
        page: 1,
        rowsPerPage: null
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar equipamentos de medição!'));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, loadEquipamentoMedicaoLista, pushErrorNotification]);

  //#endregion

  const handleDownloadFile = useCallback(async (idEquipamentoMedicao: number, nroArquivo: number, nomeArquivo: string) => {
    try {
      setCustomLoading(true);

      await downloadEquipamentoMedicaoArquivoUseCase.downloadEquipamentoMedicaoArquivo(
        idEquipamentoMedicao, nroArquivo, nomeArquivo);

    } catch (e: unknown) {
      pushErrorNotification('erro', processError(e, 'Falha ao baixar arquivo!'));
    } finally {
      setCustomLoading(false);
    }
  }, [
    downloadEquipamentoMedicaoArquivoUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleHistoricoEquipamentoMedicaoOpenState = useCallback(async (idEquipamentoMedicao: number, openState: boolean) => {
    if (openState) {
      try {
        setLoading(true);
        const results = await getAllEquipamentoMedicaoHistoricoUseCase
          .getAllEquipamentoMedicaoHistorico(idEquipamentoMedicao);
        setEquipamentoMedicaoHistoricoLista(results);
      } catch (e: unknown) {
        pushErrorNotification('erro', processError(e, 'Falha ao baixar arquivo!'));
      } finally {
        setLoading(false);
      }
    }
    setOpenHistoricoModal(openState);
  }, [
    getAllEquipamentoMedicaoHistoricoUseCase,
    pushErrorNotification,
  ]);

  const handleCopyEquipamentoMedicaoOpenState = useCallback(async (id: number | null, openState: boolean) => {
    if (saving) return;
    if (!openState) {
      setOpenCopyModal(false);
      setSelectedCopyIdEquipamentoMedicao(null);
      setDdlFuncionarios([]);
      return;
    }
    if (id === null) return;

    setSelectedCopyIdEquipamentoMedicao(id);
    setOpenCopyModal(true);
    setLoading(true);

    try {
      const funcionarios = await getFuncionariosDropDownUseCase.getFuncionariosDropDown(undefined, true, false, undefined);
      setDdlFuncionarios(funcionarios.filter(funcionario => funcionario.canBeSelected));
    } catch (e: unknown) {
      setOpenCopyModal(false);
      setSelectedCopyIdEquipamentoMedicao(null);
      pushErrorNotification("Erro", processError(e, "Falha ao buscar responsáveis!"));
    } finally {
      setLoading(false);
    }
  }, [
    saving,
    getFuncionariosDropDownUseCase,
    pushErrorNotification,
  ]);

  const handleCopyEquipamentoMedicaoSubmit = useCallback(async (form: CopyEquipamentoMedicaoForm) => {
    if (selectedCopyIdEquipamentoMedicao === null || saving) return;
    try {
      setSaving(true);
      const equipamento = await getByIdEquipamentoMedicaoUseCase.getByIdEquipamentoMedicao(selectedCopyIdEquipamentoMedicao);
      await createEquipamentoMedicaoUseCase.createEquipamentoMedicao(
        equipamento.idObra ?? null,
        form.equipamento,
        equipamento.idFuncionario ?? undefined,
        equipamento.idFuncionarioMonit ?? undefined,
        equipamento.descResponsavelMonit ?? undefined,
        equipamento.responsavelCadastradoMonit ?? undefined,
        equipamento.frequenciaVerificao ?? undefined,
        form.tipoRespEquipamento === 0 ? form.idFuncionarioResp : null,
        form.tipoRespEquipamento === 1 ? form.descResponsavelResp : undefined,
        form.tipoRespEquipamento === 0,
        equipamento.dataVerificacao ? new Date(equipamento.dataVerificacao) : undefined,
        equipamento.dataProxVerificacao ? new Date(equipamento.dataProxVerificacao) : undefined,
        equipamento.tipoMonitoramento ?? undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        equipamento.observacao ?? undefined,
        equipamento.idCentroCusto ?? undefined,
        equipamento.idFornecedorMonit ?? undefined,
        equipamento.tipoVerificacao ?? undefined,
        form.tipoRespEquipamento,
        equipamento.idFornecedorMonit != null ? 0 : equipamento.idFuncionarioMonit != null ? 1 : 3,
        equipamento.frequenciaVerificao != null ? 1 : 0,
      );
      setOpenCopyModal(false);
      setSelectedCopyIdEquipamentoMedicao(null);
      setDdlFuncionarios([]);
      await loadEquipamentoMedicaoLista();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao copiar equipamento de medição!"));
    } finally {
      setSaving(false);
    }
  }, [
    saving,
    selectedCopyIdEquipamentoMedicao,
    getByIdEquipamentoMedicaoUseCase,
    createEquipamentoMedicaoUseCase,
    loadEquipamentoMedicaoLista,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    equipamentoMedicaoLista: equipamentoMedicaoLista ?? [],
    equipamentoMedicaoHistoricoLista: equipamentoMedicaoHistoricoLista ?? [],
    openCreateModal,
    openDeleteModal,
    openHistoricoModal,
    openCopyModal,
    ddlFuncionarios,
    selectedEquipamentoMedicao,
    ddlCentroCusto,
    pagination,
    filters,

    novoEquipamento,
    handleEquipamentoMedicaoCadastroOpenState,
    handleEquipamentoMedicaoSubmit,
    handleBack,
    handleDeleteEquipamentoMedicaoOpenState,
    handleDeleteEquipamentoMedicaoSubmit,
    handleReload,
    handlePageChange,
    handleRowsPerPageChange,
    handleDataCalibracaoRangeChange,
    handleEquipamentoChange,
    handleResponsavelPeloEquipamentoChange,
    handleStatusChange,
    handleIdsCentroCustoChange,
    handleClearFilters,
    handleDownloadFile,
    handleHistoricoEquipamentoMedicaoOpenState,
    handleCopyEquipamentoMedicaoOpenState,
    handleCopyEquipamentoMedicaoSubmit,
  };
};
